import React, { useRef, useEffect } from 'react';
import styles from './OTPInput.module.css';

interface OTPInputProps {
    otp: string[]; // OTP is now passed as prop
    setOtp: (newOtp: string[]) => void; // setOtp function passed to update OTP
    className?: string;
    responsive?: boolean;
    disabled?: boolean;
}

const OTPInput = ({ otp, setOtp, className = '', responsive = false, disabled = false }: OTPInputProps) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

    const handleChange = (value: string, index: number) => {
        if (/^[0-9]$/.test(value) || value === '') {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
        }
    };

    const handleBackspace = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            (document.getElementById(`otp-input-${index - 1}`) as HTMLInputElement).focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();

        if (/^\d{6}$/.test(pastedData)) {
            const newOtp = pastedData.split('').slice(0, 6);
            setOtp(newOtp);
        }
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const otpInput = document.getElementById(`otp-input-0`) as HTMLInputElement;
            if (otpInput) {
                otpInput.focus();
            }
        }
    }, []);

    useEffect(() => {
        if (otp[0] === '') {
            inputRefs.current[0]?.focus();
        } else {
            const nextEmptyInputIndex = otp.findIndex((digit) => digit === '');
            if (nextEmptyInputIndex >= 0) {
                inputRefs.current[nextEmptyInputIndex]?.focus();
            } else {
                inputRefs.current[5]?.focus();
            }
        }
    }, [otp]);

    return (
        <div className={`${styles.otpContainer} ${className} ${responsive ? styles.responsive : ''}`}>
            {otp.map((value, index) => (
                <React.Fragment key={index}>
                    <input
                        id={`otp-input-${index}`}
                        type="number"
                        inputMode="numeric"
                        ref={(el) => (inputRefs.current[index] = el)}
                        maxLength={1}
                        value={value}
                        onPaste={handlePaste}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onKeyDown={(e) => handleBackspace(e, index)}
                        className={styles.otpInput}
                        disabled={disabled}
                    />
                    {index === 2 && <div className={styles.dashDiv}></div>}
                </React.Fragment>
            ))}
        </div>
    );
};

export default OTPInput;
